<template>
    <Head/>
    <div class="task__details">
        <div class="wrapper" id="modal">
            <div role="alert" class="container">
                <div v-if="loading" class="content">
                    <div role="status" class="td__loader">
                        <span class="sr-only">Loading...</span>
                    </div>
                </div>
                <div v-else class="content w-full">
                    <div class="close_area">
                        <div class="wrap">
                            <span class="close__b">
                                <Icon class="h-6 w-6" name="close" />

                            </span>
                        </div>
                    </div>
                    <div class="m__body w-full">
                        <main class="main">
                            <h1 class="text-lg font-bold mb-4">{{schedulerTitle}}</h1>

                            <div v-if="parsedDetails.length">
                                <div v-for="(draft, draftIndex) in parsedDetails" :key="draftIndex" class="mb-6 border p-4 rounded-lg">
                                    <div class="flex items-center">
                                        <input type="checkbox" :checked="isDraftSelected(draftIndex)" @change="toggleDraftSelection(draftIndex)">
                                        <h3 class="font-bold ml-2">Topic: {{ draft.title }}</h3>
                                    </div>

                                    <div v-for="(topic, topicIndex) in draft.parsedTopics" :key="topicIndex" class="topic-item mb-4 pl-4">
                                        <div class="flex items-center">
                                            <input type="checkbox" :checked="isTopicSelected(draftIndex, topicIndex)" @change="toggleTopicSelection(draftIndex, topicIndex)">
                                            <p class="ml-2"><strong>Name:</strong> {{ topic.name }}</p>
                                        </div>

                                        <ul class="mt-2 pl-4 list-disc">
                                            <li v-for="(point, pointIndex) in topic.points" :key="pointIndex" class="flex items-center">
                                                <input type="checkbox" v-model="selectedPoints[draftIndex][topicIndex][pointIndex]" @change="updateParentSelection(draftIndex, topicIndex)">
                                                <span class="ml-2">{{ point }}</span>
                                            </li>
                                        </ul>
                                    </div>

                                    <p class="font-bold mt-4">Class: {{ draft.selectedClassName || 'No Class Assigned' }}</p>
                                </div>
                            </div>
                            <p v-else class="text-gray-500">No details available.</p>

                            <button class="bg-blue-500 text-white px-4 py-2 rounded mt-6" @click="downloadPdf">Download PDF</button>
                        </main>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {Head, Link} from '@inertiajs/vue3'
import Icon from '@/Shared/Icon';
import { jsPDF } from "jspdf";
import Loader from '@/Shared/Loader'
import '@vuepic/vue-datepicker/dist/main.css'
import TextInput from '@/Shared/TextInput'
import TextareaInput from '@/Shared/TextareaInput'

export default {
    props: {
        isPopup: Boolean,
        parsedDetails: {
            type: Array,
            required: true
        },
        schedulerTitle: {
            type: String,
            default: "Scheduler Details"
        }
    },
    emits: ['closeModal'],
    name: 'scheduler-details-print',
    components:{  TextInput, TextareaInput,
        Icon, Loader, Link, Head},
    data() {
        return {
            selectedPoints: this.initializeSelection(),
        };
    },
    watch: {
        parsedDetails: {
            handler() {
                this.selectedPoints = this.initializeSelection();
            },
            deep: true
        }
    },
    methods: {
        initializeSelection() {
            return this.parsedDetails.map(draft =>
                draft.parsedTopics.map(topic =>
                    topic.points.map(() => false)
                )
            );
        },
        isDraftSelected(draftIndex) {
            return this.selectedPoints[draftIndex].some(topic =>
                topic.some(point => point)
            );
        },
        isTopicSelected(draftIndex, topicIndex) {
            return this.selectedPoints[draftIndex][topicIndex].some(point => point);
        },
        toggleDraftSelection(draftIndex) {
            const isSelected = !this.isDraftSelected(draftIndex);
            this.selectedPoints[draftIndex] = this.selectedPoints[draftIndex].map(topic =>
                topic.map(() => isSelected)
            );
        },
        toggleTopicSelection(draftIndex, topicIndex) {
            const isSelected = !this.isTopicSelected(draftIndex, topicIndex);
            this.selectedPoints[draftIndex][topicIndex] = this.selectedPoints[draftIndex][topicIndex].map(() => isSelected);
        },
        updateParentSelection(draftIndex, topicIndex) {
            if (this.isTopicSelected(draftIndex, topicIndex)) {
                return;
            }
            if (!this.selectedPoints[draftIndex][topicIndex].some(p => p)) {
                this.selectedPoints[draftIndex][topicIndex] = this.selectedPoints[draftIndex][topicIndex].map(() => false);
            }
        },
        downloadPdf() {
            const selectedData = this.parsedDetails.map((draft, draftIndex) => ({
                title: draft.title,
                parsedTopics: draft.parsedTopics
                    .map((topic, topicIndex) => ({
                        name: topic.name,
                        points: topic.points.filter((_, pointIndex) => this.selectedPoints[draftIndex][topicIndex][pointIndex])
                    }))
                    .filter(topic => topic.points.length > 0),
                selectedClassName: draft.selectedClassName
            })).filter(draft => draft.parsedTopics.length > 0);

            if (selectedData.length === 0) {
                alert("No items selected for PDF.");
                return;
            }

            const doc = new jsPDF();
            let y = 10;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(16);
            doc.text(`${this.schedulerTitle}`, 10, y);
            y += 10;

            doc.setFontSize(12);
            doc.setFont("helvetica", "normal");

            selectedData.forEach((draft, draftIndex) => {
                doc.setFont("helvetica", "bold");
                doc.text(`Topic: ${draft.title}`, 10, y);
                y += 8;

                draft.parsedTopics.forEach((topic, topicIndex) => {
                    doc.setFont("helvetica", "italic");
                    doc.text(`- Name: ${topic.name}`, 15, y);
                    y += 6;

                    topic.points.forEach(point => {
                        doc.setFont("helvetica", "normal");
                        doc.text(`  • ${point}`, 20, y);
                        y += 5;
                    });
                    y += 4;
                });

                doc.setFont("helvetica", "bold");
                doc.text(`Class: ${draft.selectedClassName || 'No Class Assigned'}`, 10, y);
                y += 8;
            });

            doc.save("Scheduler_Details.pdf");
        }
    },
    mounted() {
        let self = this
        window.addEventListener('keyup', function (ev) {
            if (ev.key === 'Escape') {
                if (self.isPopup) {
                    self.$emit('closeModal', true)
                } else {
                    self.goToLink(self.route(self.view === 'table' ? 'projects.view.table' : 'projects.view.board', task.project.slug || task.project.id))
                }
            }
        })
    },
};
</script>
